import { STAGES, leadsAtStage } from './leadStages';
import type { Stage } from '../../../shared/enums';
import type { Lead, Visit } from '../../../shared/types';

export interface StageGroup {
  stage: Stage;
  leads: Lead[];
}

/** Lowercase without accents: "Bahía" and "Bahia" compare equal. */
export const fold = (s: string): string =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

/** Case- and accent-insensitive search over name / owner / address / neighborhood / phone. */
export const searchLeads = (leads: readonly Lead[], query: string): Lead[] => {
  const q = fold(query.trim());
  if (!q) return [...leads];
  const terms = q.split(/\s+/);
  return leads.filter((l) => {
    const hay = fold(
      [l.business_name, l.owner_name, l.address, l.neighborhood, l.phone, l.instagram].filter(Boolean).join(' '),
    );
    return terms.every((t) => hay.includes(t));
  });
};

export const sortByName = (leads: readonly Lead[]): Lead[] =>
  [...leads].sort((a, b) => a.business_name.localeCompare(b.business_name, undefined, { sensitivity: 'base' }));

/** Leads grouped in pipeline order; empty stages are kept (with an empty array). */
export const groupByStage = (leads: readonly Lead[]): StageGroup[] =>
  STAGES.map((stage) => ({ stage, leads: sortByName(leadsAtStage(leads, stage)) }));

/** Map lead_id → last visit timestamp. */
export const lastVisitAt = (visits: readonly Visit[]): Map<string, string> => {
  const m = new Map<string, string>();
  for (const v of visits) {
    const cur = m.get(v.lead_id);
    if (!cur || v.visited_at > cur) m.set(v.lead_id, v.visited_at);
  }
  return m;
};

/** Leads by most recent activity (last visit, else created_at) — the quick-log "recent" list. */
export const recentLeads = (leads: readonly Lead[], visits: readonly Visit[], limit = 8): Lead[] => {
  const last = lastVisitAt(visits);
  const activity = (l: Lead) => {
    const lv = last.get(l.id);
    return lv && lv > l.created_at ? lv : l.created_at;
  };
  return [...leads].sort((a, b) => (activity(a) < activity(b) ? 1 : activity(a) > activity(b) ? -1 : 0)).slice(0, limit);
};
